import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Check, X } from "lucide-react"
import { useLayoutEffect, useRef, useState } from "react"

/** `ConditionField.kind === "date"` renders a native `<input type="date">`,
 *  which silently blanks any value it cannot parse, so a field only
 *  actually renders as a date input while its PERSISTED value is either
 *  empty or already `YYYY-MM-DD`. A legacy value entered before the field
 *  became a date input (e.g. German "31.12.2027", typed back when this was
 *  a plain text box) keeps rendering as text so the reviewer still sees it,
 *  rather than disappearing behind an empty picker. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Shape AND calendar validity: "2027-02-30" or "2027-13-01" pass the regex
 *  but a native date input blanks them exactly like a German date, so they
 *  must be treated as "not ISO" (rendered as text, never one-click accepted).
 *  The UTC round-trip rejects any component the Date constructor rolled
 *  over (review, wave 2). */
export function isIsoCalendarDate(value: string): boolean {
  if (!ISO_DATE_RE.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  )
}

/** Controlled locally, committed on blur: mirrors v1 Screen C's own pattern
 *  (`onChange` updates local state only, `onBlur` fires the save with
 *  `confirm:false`, "a blur must never start work" beyond a plain save). Seeded
 *  once from the merged row value; a SUCCESSFUL save round-trips the SAME
 *  value back through the cache, so there is nothing to resync there. A
 *  REJECTED save is different: `value` never moves (the cache is only
 *  written on success), so this field has no way to notice on its own and
 *  would keep showing the typed-but-lost text forever, so the parent forces a
 *  resync by changing this instance's `key` (`revertKey`, above), which
 *  remounts it and re-seeds `local` from the untouched, correct `value`. */
export function EditableField({
  label,
  hint,
  required,
  value,
  placeholder,
  suggestion,
  error = false,
  kind = "text",
  options,
  rejectedHint,
  otherOption,
  warning,
  suggestionNote,
  compact = false,
  onCommit,
}: {
  label: string
  hint?: string
  required?: boolean
  value: string
  /** The substance table's cell mode (`SubstanceEditor`): the column head
   *  already names the field, so the label is rendered for screen readers
   *  only (`sr-only`, still the `<label>`'s own text) and the required star
   *  is left to the attention border. Everything else (commit on blur,
   *  suggestion accept/decline, revert-by-key) is unchanged. */
  compact?: boolean
  /** The extracted-but-not-yet-persisted value(s) for this field, shown via
   *  the native `<input placeholder>`: greyed-out, never mistakable for a
   *  saved value, and gone the moment `value` stops being empty. The
   *  caller (`SubstanceEditor`) only ever passes this for the
   *  concentration field, and only while its persisted value is `""`. */
  placeholder?: string
  /** The extracted document value as a ONE-CLICK ACCEPT: the placeholder
   *  alone made the reviewer retype what the document already said
   *  (product-owner, 28.08.). Only ever passed while the persisted value is
   *  empty and the extraction yielded exactly one value; accepting commits
   *  through the SAME path a typed value takes. */
  suggestion?: string
  /** B7: this identity's most recent save failed and hasn't been
   *  re-committed successfully since. Drawn from the PARENT's `fieldErrors`
   *  (see `useConditionFieldWrites`' own doc), never local state, since a
   *  revert remounts this component and would wipe it. */
  error?: boolean
  /** `"date"` renders a native `<input type="date">` instead of plain text
   *  (TUR-1211 W2-7). The caller has already resolved whether this is safe
   *  for the current persisted value (see `ISO_DATE_RE`'s doc comment); this
   *  component just draws whichever type it is told. Default `"text"`. */
  kind?: "text" | "date"
  /** A fixed choice: renders a dropdown over these already-translated options
   *  instead of a text box (`ConditionField.kind === "select"`). `value` is what
   *  is stored. A persisted value that is none of them (legacy free text) is
   *  listed as one more option, shown as it is, so it is never hidden or lost.
   *  The choice commits as soon as it is made, there being no typing to wait
   *  out. Takes precedence over `kind`. */
  options?: readonly { value: string; label: string }[]
  /** The already-composed sentence explaining a fragment the extraction
   *  refused to accept as a value for this field (TUR-1211 W4-5). This
   *  component does no lookup and no formatting of its own; the caller
   *  passes the finished string, or nothing. Read-only: no control, no
   *  focus stop, shown regardless of whether `error` is also set (the two
   *  say different things). */
  rejectedHint?: string
  /** A dropdown that also takes free text: one more choice, `label`, switches the field
   *  to a text box with `warning` beneath it. Only with `options`. */
  otherOption?: { value: string; label: string; warning: string }
  /** A finished sentence flagging the persisted value (e.g. not a register entry). */
  warning?: string
  /** Shown under the suggestion while it is offered: what it is and where it came from. */
  suggestionNote?: string
  onCommit: (value: string) => void
}) {
  const { translate } = useTranslation()
  const [local, setLocal] = useState(value)
  // THE TEXT FIELD GROWS WITH ITS VALUE (TUR-1211, Ferdi 13.09): a single-line
  // input clipped "zulässigen Grenzwerten (POP-Verordnung …" with no way to
  // read the rest, so the reviewer could not even tell the value was wrong.
  // A one-row textarea sized to its content after every keystroke, plus the
  // full value as the native title, so a long value is always readable in
  // place and on hover. Dates keep the native date input (W2-7).
  const textRef = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const el = textRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }, [local])
  // B1: attention-marked while required and still empty. Read off `local`, NOT
  // the `value` prop: `local` is what the field is CURRENTLY showing (seeded
  // from `value`, then whatever the reviewer has typed since, committed or
  // not), so the marking drops the instant a keystroke lands: "the marking
  // must disappear the moment the field has a value": rather than lingering
  // until blur commits it. A save error always wins the border color: red is
  // reserved for a failed save, attention only ever asks for input.
  const requiredEmpty = Boolean(required) && local === ""
  // Declining hides the suggestion (and its placeholder) for good in this
  // mount: the reviewer said "not that value", so it must stop nagging.
  // Purely local: nothing is persisted until a value is committed.
  const [suggestionDismissed, setSuggestionDismissed] = useState(false)
  const showSuggestion =
    suggestion !== undefined && local === "" && !suggestionDismissed
  // Free text chosen from a dropdown (`otherOption`): the field becomes a text box.
  const [otherMode, setOtherMode] = useState(false)
  const asSelect = options !== undefined && !otherMode
  const labelSpan = (
    <span>
      {label}
      {required && (
        <span className="sv2-req" aria-hidden="true">
          {" "}
          *
        </span>
      )}
    </span>
  )
  return (
    <label className={cn("sv2-cf", compact && "sv2-cfc")}>
      {compact ? (
        // Still the <label>'s own text (so the control is labelled for
        // assistive tech and `getByLabelText` alike), just not drawn: the
        // table's column head is the visible label.
        <span className="sr-only">{label}</span>
      ) : hint ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>{labelSpan}</TooltipTrigger>
            <TooltipContent side="top">{hint}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        labelSpan
      )}
      {/* Accept/decline live INSIDE the field: the suggested document value
          sits in the box (placeholder), so its verdict belongs there too. */}
      <span className="sv2-cfrow">
        {asSelect ? (
          <>
            <select
              value={local}
              aria-invalid={error || undefined}
              className={cn(
                showSuggestion && "sv2-cfin-suggest",
                error && "border-red1",
                !error && requiredEmpty && "border-attention",
              )}
              onChange={(e) => {
                const next = e.target.value
                if (otherOption && next === otherOption.value) {
                  setOtherMode(true)
                  return
                }
                setLocal(next)
                if (next !== value) onCommit(next)
              }}
            >
              <option value="">
                {translate("MC_DECL_V2_COND_SELECT_EMPTY")}
              </option>
              {local !== "" && !options.some((o) => o.value === local) && (
                <option value={local}>{local}</option>
              )}
              {options!.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
              {otherOption && (
                <option value={otherOption.value}>{otherOption.label}</option>
              )}
            </select>
            {/* A dropdown shows no placeholder either: the extracted value(s)
                stay visible beside it until a choice is made. */}
            {local === "" && placeholder && !suggestionDismissed && (
              <span className="sv2-cfdatehint" data-testid="select-hint">
                {placeholder}
              </span>
            )}
          </>
        ) : kind === "date" ? (
          <>
            <input
              type="date"
              value={local}
              aria-invalid={error || undefined}
              className={cn(
                showSuggestion && "sv2-cfin-suggest",
                error && "border-red1",
                !error && requiredEmpty && "border-attention",
              )}
              onChange={(e) => setLocal(e.target.value)}
              onBlur={() => {
                if (local !== value) onCommit(local)
              }}
            />
            {/* A native date input shows no placeholder, so the extracted
                value(s) would be invisible until accepted (review,
                wave 2): render the hint as text beside the picker instead. */}
            {local === "" && placeholder && !suggestionDismissed && (
              <span className="sv2-cfdatehint" data-testid="date-hint">
                {placeholder}
              </span>
            )}
          </>
        ) : (
          <textarea
            ref={textRef}
            rows={1}
            value={local}
            title={local || undefined}
            placeholder={suggestionDismissed ? undefined : placeholder}
            aria-invalid={error || undefined}
            className={cn(
              "sv2-cfta",
              showSuggestion && "sv2-cfin-suggest",
              error && "border-red1",
              !error && requiredEmpty && "border-attention",
            )}
            onChange={(e) => setLocal(e.target.value)}
            onKeyDown={(e) => {
              // A condition value is one line: Enter commits (via blur, the
              // ONE commit path) instead of inserting a newline. Not while an
              // IME composition is open: there Enter confirms the candidate
              // (JA reviewers), and blurring would save the half-typed text
              // (review, wave 2), the same guard prompt-input.tsx uses.
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault()
                e.currentTarget.blur()
              }
            }}
            onBlur={() => {
              if (local !== value) onCommit(local)
              // Back to the dropdown: the stored value shows there, as an entry or flagged.
              if (otherMode) setOtherMode(false)
            }}
          />
        )}
        {showSuggestion && (
          <span
            className={cn("sv2-cfsugact", asSelect && "sv2-cfsugact-select")}
          >
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="sv2-cfsugbtn sv2-yes"
                    aria-label={translate("MC_DECL_V2_COND_ACCEPT_SUGGESTION")}
                    onClick={() => {
                      setLocal(suggestion)
                      onCommit(suggestion)
                    }}
                  >
                    <Check size={13} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {translate("MC_DECL_V2_COND_ACCEPT_SUGGESTION_TITLE")}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="sv2-cfsugbtn sv2-no"
                    aria-label={translate("MC_DECL_V2_COND_DECLINE_SUGGESTION")}
                    onClick={() => setSuggestionDismissed(true)}
                  >
                    <X size={13} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {translate("MC_DECL_V2_COND_DECLINE_SUGGESTION_TITLE")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        )}
      </span>
      {showSuggestion && suggestionNote && (
        <p className="sv2-cfrejecthint" data-testid="suggestion-note">
          {suggestionNote}
        </p>
      )}
      {otherMode && otherOption && (
        <p className="sv2-cfrejecthint" data-testid="other-warning">
          {otherOption.warning}
        </p>
      )}
      {warning && !otherMode && (
        <p className="sv2-cfrejecthint" data-testid="value-warning">
          {warning}
        </p>
      )}
      {rejectedHint && (
        <p className="sv2-cfrejecthint" data-testid="rejected-hint">
          {rejectedHint}
        </p>
      )}
      {error && (
        <p className="mt-1 text-[11px] text-red1">
          {translate("MC_DECL_V2_COND_FIELD_SAVE_FAILED")}
        </p>
      )}
    </label>
  )
}
